import i18n from '@/utils/i18n';
import validator from 'validator';
import { Input } from '@usepepper/sprout-kit-ui';
import { OrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { PAR_COLUMN_WIDTH } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/order-guides-list-constants';
import { useState } from 'react';

const MAX_PAR_LEVEL = 999;

const toInputValue = (parLevel: number | null): string =>
  parLevel === null ? '' : String(parLevel);

const isAllowedParInput = (text: string): boolean =>
  text === '' || validator.isInt(text, { max: MAX_PAR_LEVEL, min: 0 });

interface OrderGuideParInputProps {
  isEditable: boolean;
  item: OrderGuideItem;
  /** Fires when the user commits a par level (blur / submit) for this item. */
  onCommitParLevel: (variantPackUUID: string, parLevel: number | null) => void;
}

export const OrderGuideParInput: React.FC<OrderGuideParInputProps> = ({
  isEditable,
  item,
  onCommitParLevel,
}) => {
  const [committedParLevel, setCommittedParLevel] = useState(item.parLevel);
  const [draftParLevel, setDraftParLevel] = useState(
    toInputValue(item.parLevel),
  );

  if (item.parLevel !== committedParLevel) {
    setCommittedParLevel(item.parLevel);
    setDraftParLevel(toInputValue(item.parLevel));
  }

  const handleChangeText = (text: string) => {
    if (isAllowedParInput(text)) {
      setDraftParLevel(text);
    }
  };

  const commitParLevel = () => {
    const value =
      draftParLevel === '' ? null : validator.toInt(draftParLevel, 10);
    setDraftParLevel(toInputValue(value));
    onCommitParLevel(item.variantPackUUID, value);
  };

  const accessibilityLabel = [
    item.displayName,
    item.packDisplayString,
    i18n.t('par_level', { number: item.parLevel ?? '' }),
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Input
      accessibilityLabel={accessibilityLabel}
      disabled={!isEditable}
      keyboardType="number-pad"
      onBlur={commitParLevel}
      onChangeText={handleChangeText}
      onSubmitEditing={commitParLevel}
      paddingHorizontal={0}
      size="$md"
      textAlign="center"
      value={draftParLevel}
      width={PAR_COLUMN_WIDTH}
    />
  );
};
